import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import { useAuthStore } from '@/store/authStore';
import { 
  FiMenu, 
  FiX, 
  FiChevronDown, 
  FiUser, 
  FiPackage, 
  FiVideo, 
  FiImage, 
  FiLogOut,
  FiHome,
  FiCompass,
  FiShoppingBag,
  FiInfo,
  FiSettings
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useDemoMode } from '@/context/DemoModeContext';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const { isConnected } = useAccount();
  const { user, isAuthenticated, logout } = useAuthStore();
  const location = useLocation();
  const { isDemoMode } = useDemoMode();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const handleLogout = () => {
    logout();
    setProfileDropdownOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 shadow-3d-strong ${isScrolled ? 'glass-nav py-2' : 'py-4 bg-dark-900/80 backdrop-blur-md'}`}
    >
      <div className="container-custom flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <div className="flex items-center">
            <motion.div 
              className="h-10 w-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg flex items-center justify-center text-white font-bold text-xl mr-2 shadow-3d"
              whileHover={{ rotate: 5 }}
            >
              FT
            </motion.div>
            <span className="text-white font-bold text-xl hidden sm:block text-gradient">FTTF.World</span>
            {isDemoMode && (
              <span className="ml-2 text-xs bg-primary-600/20 text-primary-400 px-2 py-0.5 rounded-full shadow-inner-3d">
                Demo Mode
              </span>
            )}
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          <NavLink 
            to="/" 
            end
            className={({ isActive }) => 
              `nav-link relative group ${isActive ? 'text-primary-500 font-medium' : 'text-gray-400 hover:text-primary-400'}`
            }
          >
            <FiHome className="mr-1.5" size={16} />
            <span>Home</span>
            <motion.span 
              className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              initial={{ scaleX: 0 }}
              animate={location.pathname === '/' ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          </NavLink>
          <NavLink 
            to="/explore" 
            className={({ isActive }) => 
              `nav-link relative group ${isActive ? 'text-primary-500 font-medium' : 'text-gray-400 hover:text-primary-400'}`
            }
          >
            <FiCompass className="mr-1.5" size={16} />
            <span>Explore</span>
            <motion.span 
              className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              initial={{ scaleX: 0 }}
              animate={location.pathname === '/explore' ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          </NavLink>
          <NavLink 
            to="/streams" 
            className={({ isActive }) => 
              `nav-link relative group ${isActive ? 'text-primary-500 font-medium' : 'text-gray-400 hover:text-primary-400'}`
            }
          >
            <FiVideo className="mr-1.5" size={16} />
            <span>Live Streams</span>
            <motion.span 
              className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              initial={{ scaleX: 0 }}
              animate={location.pathname === '/streams' ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          </NavLink>
          <NavLink 
            to="/marketplace" 
            className={({ isActive }) => 
              `nav-link relative group ${isActive ? 'text-primary-500 font-medium' : 'text-gray-400 hover:text-primary-400'}`
            }
          >
            <FiShoppingBag className="mr-1.5" size={16} />
            <span>NFT Marketplace</span>
            <motion.span 
              className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              initial={{ scaleX: 0 }}
              animate={location.pathname === '/marketplace' ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          </NavLink>
          <NavLink 
            to="/about" 
            className={({ isActive }) => 
              `nav-link relative group ${isActive ? 'text-primary-500 font-medium' : 'text-gray-400 hover:text-primary-400'}`
            }
          >
            <FiInfo className="mr-1.5" size={16} />
            <span>About</span>
            <motion.span 
              className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              initial={{ scaleX: 0 }}
              animate={location.pathname === '/about' ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          </NavLink>
        </nav>

        {/* Right Side - Auth & Connect */}
        <div className="flex items-center space-x-4">
          {/* Connect Wallet Button */}
          <motion.div className="hidden md:block" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ConnectButton 
              showBalance={false}
              chainStatus="icon"
              accountStatus={{
                smallScreen: 'avatar',
                largeScreen: 'full',
              }}
            />
          </motion.div>

          {/* User Profile / Dashboard Link */}
          {isAuthenticated && (
            <div className="hidden md:block relative">
              <button 
                onClick={toggleProfileDropdown}
                className="flex items-center space-x-2 text-gray-300 hover:text-primary-400 transition-colors"
              >
                <motion.div 
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center text-white font-bold shadow-3d"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  {user?.username ? user.username[0].toUpperCase() : 'U'}
                </motion.div>
                <span className="hidden lg:inline-block text-gradient">{user?.username || 'User'}</span>
                <motion.div 
                  animate={{ rotate: profileDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiChevronDown size={16} />
                </motion.div>
              </button>

              {/* Profile Dropdown */}
              <AnimatePresence>
                {profileDropdownOpen && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-48 glass-card shadow-3d-sm py-2 z-50"
                  >
                    <Link to="/dashboard" className="dropdown-item flex items-center hover:text-primary-400">
                      <FiUser size={16} className="mr-2 text-primary-500" />
                      <span>Dashboard</span>
                    </Link>
                    <Link to="/dashboard/drops" className="dropdown-item flex items-center hover:text-primary-400">
                      <FiPackage size={16} className="mr-2 text-primary-500" />
                      <span>My Drops</span>
                    </Link>
                    <Link to="/dashboard/streams" className="dropdown-item flex items-center hover:text-primary-400">
                      <FiVideo size={16} className="mr-2 text-primary-500" />
                      <span>My Streams</span>
                    </Link>
                    <Link to="/dashboard/nfts" className="dropdown-item flex items-center hover:text-primary-400">
                      <FiImage size={16} className="mr-2 text-primary-500" />
                      <span>My NFTs</span>
                    </Link>
                    <Link to="/dashboard/settings" className="dropdown-item flex items-center hover:text-primary-400">
                      <FiSettings size={16} className="mr-2 text-primary-500" />
                      <span>Settings</span>
                    </Link>
                    {isDemoMode ? (
                       <Link to={`/profile/${user?.username || 'demo'}`} className="dropdown-item flex items-center hover:text-primary-400">
                          <FiUser size={16} className="mr-2 text-primary-500" />
                          <span>View Profile</span>
                       </Link>
                    ) : (
                      <Link to={`/profile/${user?.walletAddress}`} className="dropdown-item flex items-center hover:text-primary-400">
                         <FiUser size={16} className="mr-2 text-primary-500" />
                         <span>View Profile</span>
                      </Link>
                    )}
                    
                    <button onClick={handleLogout} className="dropdown-item flex items-center text-accent-500 hover:text-accent-400">
                      <FiLogOut size={16} className="mr-2" />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Mobile Menu Button */}
          <motion.button 
            onClick={toggleMenu} 
            className="md:hidden text-gray-400 hover:text-primary-400 transition-colors"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden glass-card border-t border-dark-700 overflow-hidden shadow-3d"
          >
            <div className="container-custom py-4">
              <nav className="flex flex-col space-y-2">
                <NavLink 
                  to="/" 
                  end
                  className={({ isActive }) => 
                    `mobile-nav-link flex items-center px-3 py-2 rounded-md transition-colors ${isActive ? 'text-primary-500 bg-dark-700 font-medium' : 'text-gray-400 hover:bg-dark-800'}`
                  }
                >
                  <FiHome className="mr-2" size={18} />
                  <span>Home</span>
                </NavLink>
                <NavLink 
                  to="/explore" 
                  className={({ isActive }) => 
                    `mobile-nav-link flex items-center px-3 py-2 rounded-md transition-colors ${isActive ? 'text-primary-500 bg-dark-700 font-medium' : 'text-gray-400 hover:bg-dark-800'}`
                  }
                >
                  <FiCompass className="mr-2" size={18} />
                  <span>Explore</span>
                </NavLink>
                <NavLink 
                  to="/streams" 
                  className={({ isActive }) => 
                    `mobile-nav-link flex items-center px-3 py-2 rounded-md transition-colors ${isActive ? 'text-primary-500 bg-dark-700 font-medium' : 'text-gray-400 hover:bg-dark-800'}`
                  }
                >
                  <FiVideo className="mr-2" size={18} />
                  <span>Live Streams</span>
                </NavLink>
                <NavLink 
                  to="/marketplace" 
                  className={({ isActive }) => 
                    `mobile-nav-link flex items-center px-3 py-2 rounded-md transition-colors ${isActive ? 'text-primary-500 bg-dark-700 font-medium' : 'text-gray-400 hover:bg-dark-800'}`
                  }
                >
                  <FiShoppingBag className="mr-2" size={18} />
                  <span>NFT Marketplace</span>
                </NavLink>
                <NavLink 
                  to="/about" 
                  className={({ isActive }) => 
                    `mobile-nav-link flex items-center px-3 py-2 rounded-md transition-colors ${isActive ? 'text-primary-500 bg-dark-700 font-medium' : 'text-gray-400 hover:bg-dark-800'}`
                  }
                >
                  <FiInfo className="mr-2" size={18} />
                  <span>About</span>
                </NavLink>
              </nav>
              {/* Mobile Wallet Connect / User Info */}
              <div className="mt-4 pt-4 border-t border-dark-700">
                 {isAuthenticated ? (
                   <div className="flex items-center justify-between">
                      <div className="flex items-center">
                         <motion.div
                            className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center text-white font-bold text-sm mr-2 shadow-3d"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                         >
                           {user?.username ? user.username[0].toUpperCase() : 'U'}
                         </motion.div>
                         <span className="text-sm text-gradient">{user?.username || 'User'}</span>
                      </div>
                      <button onClick={handleLogout} className="text-accent-500 hover:text-accent-400">
                         <FiLogOut size={18} />
                      </button>
                   </div>
                 ) : (
                   <ConnectButton 
                     showBalance={false}
                     chainStatus="icon"
                     accountStatus="avatar"
                   />
                 )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;